// src/pages/SupportPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiClock, FiMail, FiMapPin, FiPhone, FiRefreshCw, FiSearch, FiUser } from 'react-icons/fi';
import { MdDashboard } from 'react-icons/md';

import { useAuth } from '../context/AuthContext';
import { useRemoteCss } from '../hooks/useRemoteCss';
import useAppTheme from '../hooks/useAppTheme';
import axiosInstance from '../api/axiosInstance';
import CookieBanner from '../components/CookieBanner';
import ThemeDock from '../components/public/ThemeDock';
import PageLoader from '../components/public/PageLoader';

import './cx.css';
import './chrome.css';
import './support.css';

/**
 * Public Support page (read-only).
 * Shape:
 *  - New: { countryContacts: [{ country, supportNumber, supportEmail, contactPerson, supportHours, note }] }
 *  - Legacy: { supportNumber/supportEmail/... + countriesSupported[] }
 */
const emptyContact = () => ({
  country: '',
  supportNumber: '',
  supportEmail: '',
  contactPerson: '',
  supportHours: '',
  note: '',
});

function normalizeContacts(list) {
  const arr = Array.isArray(list) ? list : [];
  const seen = new Set();
  const out = [];

  for (const raw of arr) {
    const item = { ...emptyContact(), ...(raw || {}) };
    item.country = String(item.country || '').trim();
    if (!item.country) continue;

    const key = item.country.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    item.supportNumber = String(item.supportNumber || '').trim();
    item.supportEmail = String(item.supportEmail || '').trim();
    item.contactPerson = String(item.contactPerson || '').trim();
    item.supportHours = String(item.supportHours || '').trim();
    item.note = String(item.note || '').trim();

    out.push(item);
  }

  out.sort((a, b) => a.country.localeCompare(b.country));
  return out;
}

function synthesizeContactsFromLegacy(incoming) {
  const legacyCountries = Array.isArray(incoming?.countriesSupported) ? incoming.countriesSupported : [];
  const countries = legacyCountries.map((c) => String(c || '').trim()).filter(Boolean);

  const base = {
    supportNumber: String(incoming?.supportNumber || '').trim(),
    supportEmail: String(incoming?.supportEmail || '').trim(),
    contactPerson: String(incoming?.contactPerson || '').trim(),
    supportHours: String(incoming?.supportHours || '').trim(),
    note: String(incoming?.note || '').trim(),
  };

  if (!countries.length) return [];
  return normalizeContacts(countries.map((country) => ({ country, ...base })));
}

const telHref = (n) => `tel:${String(n).replace(/[^\d+]/g, '')}`;

const Fact = ({ icon, label, children }) => (
  <div className="support-fact">
    <dt>
      {icon} {label}
    </dt>
    <dd>{children || <span className="support-muted">Not set</span>}</dd>
  </div>
);

const ContactCard = ({ c }) => (
  <article className="support-card">
    <header className="support-card-head">
      <span className="support-card-icon" aria-hidden="true">
        <FiMapPin />
      </span>
      <h3 className="support-card-title">{c.country}</h3>
    </header>

    {(c.supportNumber || c.supportEmail) && (
      <div className="support-card-actions">
        {c.supportNumber && (
          <a className="support-btn" href={telHref(c.supportNumber)}>
            <FiPhone /> Call
          </a>
        )}
        {c.supportEmail && (
          <a className="support-btn support-btn--outline" href={`mailto:${c.supportEmail}`}>
            <FiMail /> Email
          </a>
        )}
      </div>
    )}

    <dl className="support-facts">
      <Fact icon={<FiPhone />} label="Phone">
        {c.supportNumber && <a href={telHref(c.supportNumber)}>{c.supportNumber}</a>}
      </Fact>
      <Fact icon={<FiMail />} label="Email">
        {c.supportEmail && <a href={`mailto:${c.supportEmail}`}>{c.supportEmail}</a>}
      </Fact>
      <Fact icon={<FiUser />} label="Contact">
        {c.contactPerson}
      </Fact>
      <Fact icon={<FiClock />} label="Hours">
        {c.supportHours}
      </Fact>
    </dl>

    {c.note && <p className="support-note">{c.note}</p>}
  </article>
);

export default function SupportPage() {
  const { authData } = useAuth();
  const token = authData?.token;
  const navigate = useNavigate();

  const { theme, setTheme } = useAppTheme();

  // Optional remote CSS
  const cssUrl = `${axiosInstance.defaults.baseURL}/styles/SupportPage`;
  const cssReady = useRemoteCss(cssUrl);

  // Minimum delay so the loader doesn't flicker
  const [minDelayDone, setMinDelayDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinDelayDone(true), 300);
    return () => clearTimeout(t);
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('all');

  useEffect(() => {
    document.title = 'Nnetego | Support';

    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'Support contacts by country for Nnetego Hotel Management System.';
    document.head.appendChild(metaDescription);

    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  const fetchSupport = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosInstance.get('/pages/support');
      const incoming = res?.data || {};

      const incomingContacts = normalizeContacts(incoming?.countryContacts);
      const finalContacts = incomingContacts.length ? incomingContacts : synthesizeContactsFromLegacy(incoming);

      setContacts(finalContacts);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || e?.message || 'Failed to load support details.';
      setError(typeof msg === 'string' ? msg : 'Failed to load support details.');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupport();
  }, []);

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      contacts.filter((c) => {
        if (selected !== 'all' && c.country !== selected) return false;
        if (!q) return true;
        return `${c.country} ${c.contactPerson} ${c.supportEmail} ${c.supportNumber}`.toLowerCase().includes(q);
      }),
    [contacts, selected, q]
  );

  if (!cssReady || !minDelayDone) return <PageLoader />;

  return (
    <div className="lux-layout support-page">
      <ThemeDock theme={theme} setTheme={setTheme} />

      <div className="lux-topbar">Support Contacts by Country</div>

      <header className="lux-header support-header">
        <div className="lux-container header-inner">
          <div className="lux-logo" onClick={() => navigate('/')} role="button" tabIndex={0}>
            <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
          </div>

          <div className="header-right">
            <button className="lux-btn-ghost" onClick={() => navigate(-1)}>
              <FiArrowLeft /> Back
            </button>

            {token ? (
              <button className="lux-cta" onClick={() => navigate('/dashboard')}>
                <MdDashboard style={{ marginRight: 8 }} /> Dashboard
              </button>
            ) : (
              <button className="lux-cta" onClick={() => navigate('/login')}>
                Get Started
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="support-hero">
        <div className="lux-container">
          <span className="hero-pill">Support</span>
          <h1 className="support-hero-title">
            How can we <em>help</em>?
          </h1>
          <p className="support-hero-sub">Country-specific support contacts. Select the relevant country below to find the correct phone, email, contact person, hours, and notes.</p>

          <label className="support-search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by country or contact"
              aria-label="Search support contacts"
            />
          </label>

          {contacts.length > 1 && (
            <div className="support-chips" role="group" aria-label="Filter by country">
              <button type="button" className={`support-chip ${selected === 'all' ? 'is-active' : ''}`} aria-pressed={selected === 'all'} onClick={() => setSelected('all')}>
                All countries
              </button>
              {contacts.map((c) => (
                <button
                  key={c.country}
                  type="button"
                  className={`support-chip ${selected === c.country ? 'is-active' : ''}`}
                  aria-pressed={selected === c.country}
                  onClick={() => setSelected(c.country)}
                >
                  {c.country}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="support-body">
        <div className="lux-container">
          {error && (
            <div className="support-alert" role="alert">
              <span>{error}</span>
              <button type="button" className="support-btn support-btn--outline" onClick={fetchSupport} disabled={loading}>
                <FiRefreshCw /> Try again
              </button>
            </div>
          )}

          {loading && !contacts.length ? (
            <div className="support-grid" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="support-card support-skeleton" />
              ))}
            </div>
          ) : visible.length ? (
            <div className="support-grid">
              {visible.map((c) => (
                <ContactCard key={c.country} c={c} />
              ))}
            </div>
          ) : (
            !error && (
              <div className="support-empty">
                {contacts.length ? (
                  <>
                    <p>No country matches your search.</p>
                    <button
                      type="button"
                      className="support-btn support-btn--outline"
                      onClick={() => {
                        setQuery('');
                        setSelected('all');
                      }}
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  <p>No country contacts are configured yet.</p>
                )}
              </div>
            )
          )}

          {!!contacts.length && <p className="support-footnote">Support is available only for configured countries/regions. Please contact your administrator to add additional regions and support contacts.</p>}
        </div>
      </section>

      <footer className="lux-footer">
        <div className="lux-container">
          <div className="lux-legal">
            <div className="lux-legal-copy">
              © {new Date().getFullYear()} Nnetego. All rights reserved. <a href="/legal/privacy">Privacy</a> • <a href="/legal/terms">Terms</a>
            </div>
            <div className="lux-legal-credits">
              Designed by{' '}
              <a href="https://lanesands.com" target="_blank" rel="noopener noreferrer">
                Lanesands
              </a>
              .
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
